import { useState } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import ModuleHeader from "@/components/voff/ModuleHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, FileText, Loader2, Download, Mail } from "lucide-react";
import { toast } from "sonner";

const LIBRARY = [
  { name: "Kongsberg Maritime — Tech Services", type: "Services", party: "Kongsberg Maritime", date: "2026-04-02", risk: "MODERATE", status: "Reviewed" },
  { name: "ExxonMobil Guyana — CTV Charter", type: "Time Charter", party: "ExxonMobil", date: "2026-03-18", risk: "LOW", status: "Counsel review" },
  { name: "Azule Energy NDA", type: "NDA", party: "Azule Energy", date: "2026-03-04", risk: "LOW", status: "Executed" },
  { name: "Newbuild Hull #03 Contract", type: "Newbuilding", party: "Marin Teknikk", date: "2026-02-22", risk: "HIGH", status: "Negotiating" },
];

const FOCUS = ["Liability and indemnity","Termination rights","Payment terms","Off-hire definitions","IP and confidentiality","Dispute resolution","Force majeure","Insurance","Governing law","VOFF authority limits"];

const ContractRedline = () => {
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [type, setType] = useState("NDA");
  const [counterparty, setCounterparty] = useState("");

  const runReview = () => {
    if (!counterparty) { toast.error("Counterparty required"); return; }
    setRunning(true);
    setTimeout(() => {
      setReport({
        risk: "MODERATE", high: 3, med: 6, low: 3, ok: 47,
        clauses: [
          { ref: "8.2", title: "Liability Cap", risk: "HIGH", original: "Each party's liability shall be unlimited for any losses arising from breach.", issue: "Unlimited liability — exposes VOFF beyond insurance coverage.", suggested: "Each party's aggregate liability shall not exceed USD 5,000,000, being the value of applicable insurance.", standard: "BIMCO liability cap guidance" },
          { ref: "12.4", title: "Termination for Convenience", risk: "MEDIUM", original: "Owner may terminate without cause on 7 days' notice.", issue: "Short notice period; VOFF should require 30-90 days for charter agreements.", suggested: "Termination for convenience requires 60 days' written notice.", standard: "BIMCO SUPPLYTIME 2017" },
          { ref: "15.1", title: "Governing Law", risk: "MEDIUM", original: "This Agreement is governed by the laws of Texas.", issue: "Sole counterparty jurisdiction; VOFF prefers English law or Marshall Islands.", suggested: "This Agreement is governed by English law; disputes resolved by LMAA arbitration in London.", standard: "VOFF Standard" },
          { ref: "9.3", title: "Off-hire — Operational Standby", risk: "HIGH", original: "Off-hire applies to any period the vessel is on standby.", issue: "Operational standby is not equipment failure; VOFF should not be off-hire for client-directed standby.", suggested: "Off-hire applies only to equipment failure preventing the vessel from performing services.", standard: "VOFF Standard CP §4.2" },
          { ref: "6.1", title: "Knock-for-Knock Indemnity", risk: "LOW", original: "Each party indemnifies the other for personnel injury regardless of cause.", issue: "Acceptable — aligns with VOFF standard offshore practice.", suggested: "Acceptable as drafted.", standard: "OCIMF Knock-for-Knock" },
        ],
      });
      setRunning(false);
      toast.success("Contract review complete");
    }, 2000);
  };

  return (
    <IntranetLayout>
      <ModuleHeader title="Contract Redline Assistant" subtitle="AI-Powered Contract Review — First Pass Redline" />

      <div className="bg-[#E8A020]/10 border border-[#E8A020]/40 rounded-lg p-3 mb-6 flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-[#E8A020] mt-0.5 shrink-0" />
        <p className="text-xs text-[#0D1E2B]">This tool provides AI-assisted analysis for commercial guidance only. It is not legal advice. All final positions should be confirmed with qualified maritime legal counsel.</p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2 border-[#D0DDE6]">
          <CardContent className="p-6 space-y-4">
            <div className="border-2 border-dashed border-[#288CC8]/40 rounded-lg p-6 text-center bg-[#F4F7FA]">
              <FileText className="w-8 h-8 mx-auto text-[#288CC8] mb-2" />
              <p className="text-sm">Drop PDF/DOCX or paste text below</p>
            </div>
            <Textarea rows={5} placeholder="Or paste contract text here..." />
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Type</Label>
                <Select value={type} onValueChange={setType}><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["NDA","Time Charter","Bareboat","MOU","Services","Newbuilding","Manning"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Governing Law</Label>
                <Select defaultValue="English"><SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["English","Marshall Islands","Texas","Norwegian","Singapore"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="text-xs">Counterparty</Label><Input value={counterparty} onChange={(e) => setCounterparty(e.target.value)} placeholder="Kongsberg Maritime" /></div>
            <div>
              <Label className="text-xs mb-2 block">Review Focus</Label>
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                {FOCUS.map((f) => <label key={f} className="flex items-center gap-1.5"><Checkbox defaultChecked /><span>{f}</span></label>)}
              </div>
            </div>
            <Button onClick={runReview} disabled={running} className="w-full bg-[#288CC8] hover:bg-[#288CC8]/90 text-white">
              {running ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Reviewing...</> : "Run Contract Review"}
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 border-[#D0DDE6]">
          <CardContent className="p-6">
            {!report ? (
              <div className="text-center py-20 text-muted-foreground"><FileText className="w-16 h-16 mx-auto mb-4 text-[#288CC8]/40" /><p>Submit a contract to generate redline report</p></div>
            ) : (
              <div className="space-y-4">
                <div className="bg-[#F4F7FA] p-4 rounded-lg border border-[#D0DDE6]">
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="text-xs text-[#6B8A9A] uppercase">Review Summary</div>
                      <div className="font-bold text-[#00283C]">{type} — {counterparty}</div>
                    </div>
                    <Badge className="bg-[#E8A020] text-white">Risk: {report.risk}</Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-3 mt-4 text-center">
                    <div><div className="text-2xl font-bold text-[#C83228]">{report.high}</div><div className="text-xs">High</div></div>
                    <div><div className="text-2xl font-bold text-[#E8A020]">{report.med}</div><div className="text-xs">Medium</div></div>
                    <div><div className="text-2xl font-bold text-yellow-600">{report.low}</div><div className="text-xs">Note</div></div>
                    <div><div className="text-2xl font-bold text-[#2CA048]">{report.ok}</div><div className="text-xs">Clear</div></div>
                  </div>
                </div>
                <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
                  {report.clauses.map((c: any) => (
                    <div key={c.ref} className={`border-l-4 p-4 rounded ${c.risk === "HIGH" ? "border-[#C83228] bg-[#C83228]/5" : c.risk === "MEDIUM" ? "border-[#E8A020] bg-[#E8A020]/5" : "border-[#2CA048] bg-[#2CA048]/5"}`}>
                      <div className="flex justify-between items-center mb-2">
                        <div className="font-bold text-sm text-[#00283C]">Clause {c.ref} — {c.title}</div>
                        <Badge variant="outline" className={c.risk === "HIGH" ? "border-[#C83228] text-[#C83228]" : c.risk === "MEDIUM" ? "border-[#E8A020] text-[#E8A020]" : "border-[#2CA048] text-[#2CA048]"}>{c.risk}</Badge>
                      </div>
                      <div className="text-xs space-y-2">
                        <div><span className="font-semibold text-[#6B8A9A]">ORIGINAL:</span> <em>"{c.original}"</em></div>
                        <div><span className="font-semibold text-[#6B8A9A]">ISSUE:</span> {c.issue}</div>
                        <div><span className="font-semibold text-[#6B8A9A]">SUGGESTED:</span> "{c.suggested}"</div>
                        <div className="text-[#6B8A9A]"><span className="font-semibold">Standard:</span> {c.standard}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 pt-3 border-t border-[#D0DDE6]">
                  <Button onClick={() => toast.success("Generating: PDF", { description: "Your file will download shortly." })} size="sm" variant="outline"><Download className="w-3 h-3 mr-1" />PDF</Button>
                  <Button onClick={() => toast.success("Action recorded: To Counsel")} size="sm" variant="outline"><Mail className="w-3 h-3 mr-1" />To Counsel</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6 border-[#D0DDE6]">
        <CardContent className="p-6">
          <h3 className="font-bold text-[#00283C] mb-3">Contract Library</h3>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#D0DDE6] text-left text-xs text-[#6B8A9A] uppercase">
              <th className="py-2">Contract</th><th>Type</th><th>Counterparty</th><th>Date</th><th>Risk</th><th>Status</th></tr></thead>
            <tbody>{LIBRARY.map((c) => (
              <tr key={c.name} className="border-b border-[#D0DDE6]/50 hover:bg-[#F4F7FA]">
                <td className="py-3 font-medium text-[#00283C]">{c.name}</td><td>{c.type}</td><td>{c.party}</td><td>{c.date}</td>
                <td><Badge className={c.risk === "HIGH" ? "bg-[#C83228]" : c.risk === "MODERATE" ? "bg-[#E8A020]" : "bg-[#2CA048]"}>{c.risk}</Badge></td>
                <td className="text-xs">{c.status}</td>
              </tr>))}</tbody>
          </table>
        </CardContent>
      </Card>
    </IntranetLayout>
  );
};
export default ContractRedline;
